/**
 * @Time    : 2024/8/16 11:11
 * @Author  : tlovet
 * @Software: WebStorm
 * 枚举集合：部分声明不符合枚举类型
 */

import type { ModalProps, SelectProps } from 'antd';
import type { TextAreaProps } from 'antd/es/input/TextArea';

/** 本系统使用的Modal默认配置 */
export const DEFAULT_MODAL_CONFIG: ModalProps = {
  centered: true,
  destroyOnHidden: true,
  maskClosable: false,
  keyboard: false,
  closeIcon: true,
  width: 580, // 默认宽度520
};

/** 本系统使用的ProForm layout="horizontal"时的默认样式 */
export const DEFAULT_FORM_ITEM_LAYOUT = {
  labelCol: { span: 5 },
  wrapperCol: { offset: 0, span: 16 },
};

/**
 * 本系统使用的ProFormTextArea组件默认配置，与 Input.TextArea 相同。
 * https://ant.design/components/input-cn#inputtextarea
 * */
export const DEFAULT_TEXTAREA_CONFIG: TextAreaProps = {
  showCount: false, // 是否展示字数
  allowClear: true, // 可以点击清除图标删除内容
  autoSize: { minRows: 3, maxRows: 5 }, // 自适应内容高度，可设置为 true | false 或对象：{ minRows: 3, maxRows: 5 }
};

/** 本系统使用的默认数据 */
export enum DEFAULT_ENUM {
  DEFAULT_THEME = 'light', // 默认的系统主题
  DEFAULT_DATE_FORMAT = 'YYYY-MM-DD', // 默认的日期格式
  DEFAULT_MONTH_FORMAT = 'YYYY-MM', // 默认的月份格式
  DEFAULT_DAY_FORMAT = 'MM-DD', // 默认的天份格式
  DEFAULT_TIME_FORMAT = 'HH:mm:ss', // 默认的时间格式
  DEFAULT_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss', // 默认的日期时间格式
}

/** 常用单位 */
export enum UNITS_ENUM {
  SERVER = '台',
  CPU = '核',
  MEMORY = 'GB',
  DISK = 'GB',
  MONEY = '元',
  PERCENT = '%',
}

/** 高峰期管理-封禁-封禁类型 */
export enum FORBID_TYPE_ENUM {
  SYSTEM = '系统',
  ORG = '部门',
  APPKEY = '服务',
}

/** 高峰期管理-封禁-封禁级别 */
export enum FORBID_LEVEL_ENUM {
  GLOBAL = '强制封禁',
  NORMAL = '默认封禁', // 日常
  UNLOCK = '强制解禁',
}

/** 状态 */
export enum STATUS_ENUM {
  ONLINE = '有效',
  OFFLINE = '无效',
}

/** 常用颜色 */
export enum COLOR_ENUM {
  SUCCESS = '#52c41a',
  ERROR = '#ff4d4f',
  // ERROR = '#ff6459',
  PROCESSING = '#1677ff',
  WARNING = '#faad14',
  DISABLED = 'rgba(0, 0, 0, 0.45)',
}

/** 工单状态 */
export const WF_STATUS_ENUM: Record<
  'TODO' | 'SUCCESS' | 'UNDER_REVIEW' | 'EXECUTING' | 'REJECTED' | 'FAILED' | 'REVOKED',
  { value: number; label: string; tagColor: string }
> = {
  TODO: { value: 0, label: '待处理', tagColor: COLOR_ENUM.PROCESSING },
  SUCCESS: { value: 1, label: '已完成', tagColor: COLOR_ENUM.SUCCESS },
  UNDER_REVIEW: { value: 2, label: '审核中', tagColor: COLOR_ENUM.WARNING },
  EXECUTING: { value: 3, label: '执行中', tagColor: COLOR_ENUM.PROCESSING },
  REJECTED: { value: -1, label: '未通过', tagColor: COLOR_ENUM.ERROR },
  FAILED: { value: -2, label: '执行失败', tagColor: COLOR_ENUM.ERROR },
  REVOKED: { value: -3, label: '已撤销', tagColor: 'default' },
};

/** 工单状态 valueEnum */
export const WF_STATUS_VALUE_ENUM: Record<string, string> = {};
Object.values(WF_STATUS_ENUM).forEach((item) => {
  WF_STATUS_VALUE_ENUM[item?.value] = item?.label;
});

/** 环境 */
export const ENV_ENUM: Record<
  PolymerizeEnvType,
  {
    value: PolymerizeEnvType;
    label: string;
    envs: EnvType[];
    options: { label: string; value: EnvType; key: EnvType }[];
  }
> = {
  all: {
    value: 'all',
    label: '全部',
    envs: ['prod', 'staging', 'test', 'dev'],
    options: [
      { label: 'Prod', value: 'prod', key: 'prod' },
      { label: 'Staging', value: 'staging', key: 'staging' },
      { label: 'Test', value: 'test', key: 'test' },
      { label: 'Dev', value: 'dev', key: 'dev' },
    ],
  },
  online: {
    value: 'online',
    label: '线上',
    envs: ['prod', 'staging'],
    options: [
      { label: 'Prod', value: 'prod', key: 'prod' },
      { label: 'Staging', value: 'staging', key: 'staging' },
    ],
  },
  offline: {
    value: 'offline',
    label: '线下',
    envs: ['test', 'dev'],
    options: [
      { label: 'test', value: 'test', key: 'test' },
      { label: 'dev', value: 'dev', key: 'dev' },
    ],
  },
};
/** 环境 valueEnum */
export const ENV_VALUE_ENUM: any = {};
ENV_ENUM.all.options.forEach((item) => {
  ENV_VALUE_ENUM[item?.value] = item?.label;
});

/** 服务等级 */
export const SERVICE_RANK_ENUM: Record<
  'CORE' | 'NONCORE',
  { value: number; label: string; tagColor: string }
> = {
  CORE: {
    value: 1,
    label: '核心服务',
    tagColor: 'success',
  },
  NONCORE: {
    value: 0,
    label: '非核心服务',
    tagColor: 'processing',
  },
};

/** 服务容灾等级 */
export const SERVICE_CAPACITY_ENUM: Record<
  'ZERO' | 'ONE' | 'TWO' | 'THREE' | 'FOUR' | 'FIVE',
  { value: number; label: string; tagColor: string }
> = {
  ZERO: { value: 0, label: '0', tagColor: 'error' },
  ONE: { value: 1, label: '1', tagColor: 'error' },
  TWO: { value: 2, label: '2', tagColor: 'warning' },
  THREE: { value: 3, label: '3', tagColor: 'processing' },
  FOUR: { value: 4, label: '4', tagColor: 'success' },
  FIVE: { value: 5, label: '5', tagColor: 'processing' },
};

/** 服务状态 */
export const SERVICE_STATEFUL_ENUM: Record<
  'YES' | 'NO',
  { value: boolean; label: string; color: string; tagColor: string }
> = {
  YES: {
    value: true,
    label: '有状态',
    color: COLOR_ENUM.SUCCESS,
    tagColor: 'success',
  },
  NO: {
    value: false,
    label: '无状态',
    color: COLOR_ENUM.DISABLED,
    tagColor: 'default',
  },
};

/** 主机状态 */
export const HOST_STATUS_ENUM: Record<
  'RUNNING' | 'STOPPED' | 'WAITING_OFFLINE' | 'TERMINATED' | 'DEFAULT' | 'UNKNOWN',
  { value: string; label: string; tagColor: string }
> = {
  RUNNING: {
    value: 'RUNNING',
    label: '运行中',
    // tagColor: 'processing',
    tagColor: 'success',
  },
  STOPPED: {
    value: 'STOPPED',
    label: '关机',
    tagColor: 'error',
  },
  WAITING_OFFLINE: {
    value: 'WAITING_OFFLINE',
    label: '待下线',
    tagColor: 'default',
  },
  TERMINATED: {
    value: 'TERMINATED',
    label: '已销毁',
    tagColor: 'error',
  },
  UNKNOWN: {
    value: 'UNKNOWN',
    label: '未知状态',
    tagColor: 'warning',
  },
  DEFAULT: {
    value: 'DEFAULT',
    label: '默认状态',
    tagColor: 'warning',
  },
};

/** 创建一个映射，将字符串值映射到枚举成员: 常用单位 */
export const UNITS_MAP: Record<string, UNITS_ENUM> = {
  server: UNITS_ENUM.SERVER,
  cpu: UNITS_ENUM.CPU,
  memory: UNITS_ENUM.MEMORY,
};

/** 创建一个映射，将字符串值映射到枚举成员: 封禁类型 */
export const FORBID_TYPE_MAP: Record<string, FORBID_TYPE_ENUM> = {
  system: FORBID_TYPE_ENUM.SYSTEM,
  org: FORBID_TYPE_ENUM.ORG,
  appkey: FORBID_TYPE_ENUM.APPKEY,
};

/** 创建一个映射，将字符串值映射到枚举成员: 封禁级别 */
export const FORBID_LEVEL_MAP: Record<string, { text: string; color: string; tagColor: string }> = {
  global: { text: FORBID_LEVEL_ENUM.GLOBAL, color: COLOR_ENUM.ERROR, tagColor: 'error' },
  normal: { text: FORBID_LEVEL_ENUM.NORMAL, color: COLOR_ENUM.ERROR, tagColor: 'error' },
  unlock: { text: FORBID_LEVEL_ENUM.UNLOCK, color: COLOR_ENUM.SUCCESS, tagColor: 'success' },
};

/** 创建一个映射，将字符串值映射到枚举成员: 封禁策略状态 */
export const FORBID_STATUS_MAP: Record<string, { text: string; color: string; tagColor: string }> =
  {
    online: { text: STATUS_ENUM.ONLINE, color: COLOR_ENUM.PROCESSING, tagColor: 'processing' },
    offline: { text: STATUS_ENUM.OFFLINE, color: COLOR_ENUM.DISABLED, tagColor: 'default' },
  };

/** 用途分类 */
export const USE_TYPE_OPTIONS: SelectProps['options'] = [
  { label: '自定义变更', value: 'custom' },
  { label: '运维变更', value: 'sre' },
  { label: '发布计划', value: 'plus' },
];

/** 用途分类 valueEnum */
export const USE_TYPE_VALUE_ENUM: Record<string, string> = {};
Object.values(USE_TYPE_OPTIONS).forEach((item: any) => {
  USE_TYPE_VALUE_ENUM[item?.value] = item?.label;
});

/** 风险等级 */
export const RISK_LEVEL_OPTIONS: SelectProps['options'] = [
  { label: '低风险', value: 'low' },
  { label: '高风险', value: 'high' },
];

/** 风险等级 valueEnum */
export const RISK_LEVEL_VALUE_ENUM: Record<string, { text: string; status: string }> = {};
Object.values(RISK_LEVEL_OPTIONS).forEach((item: any) => {
  RISK_LEVEL_VALUE_ENUM[item?.value] = {
    text: item?.label,
    status: item?.value === 'low' ? 'Success' : 'Error',
  };
});

/** 变更类型 */
export const CHANGE_TYPE_OPTIONS: SelectProps['options'] = [
  { label: '常规变更', value: 'general' },
  { label: '紧急变更', value: 'emergency' },
];

/** 变更类型 valueEnum */
export const CHANGE_TYPE_VALUE_ENUM: Record<string, string> = {};
Object.values(CHANGE_TYPE_OPTIONS).forEach((item: any) => {
  CHANGE_TYPE_VALUE_ENUM[item?.value] = item?.label;
});

/** 字段类型 */
export const FIELD_TYPE_OPTIONS: SelectProps['options'] = [
  { label: '复选', value: 'checkbox' },
  { label: '多行文本', value: 'textArea' },
  { label: '下拉单选', value: 'select' },
  { label: '下拉多选', value: 'selectMultiple' },
];

/** 字段类型 valueEnum */
export const FIELD_TYPE_VALUE_ENUM: Record<string, string> = {};
Object.values(FIELD_TYPE_OPTIONS).forEach((item: any) => {
  FIELD_TYPE_VALUE_ENUM[item?.value] = item?.label;
});

/** 审批流程-审批人 */
export const WORKFLOW_REVIEWER_OPTIONS: SelectProps['options'] = [
  { label: 'QA', value: 'qa' },
  { label: 'SRE', value: 'sre' },
  { label: '直属上级', value: 'leader' },
];

/** 审批流程-审批人 valueEnum */
export const WORKFLOW_REVIEWER_VALUE_ENUM: Record<string, string> = {};
Object.values(WORKFLOW_REVIEWER_OPTIONS).forEach((item: any) => {
  WORKFLOW_REVIEWER_VALUE_ENUM[item?.value] = item?.label;
});

/** 审批流程-审批模式 */
export const WORKFLOW_AUDIT_MODE_OPTIONS: SelectProps['options'] = [
  { label: '或签，任意一名审批人同意', value: 'or' },
  { label: '会签，需要所有审批人同意', value: 'and', disabled: true },
];

/** 审批流程-审批模式 valueEnum */
export const WORKFLOW_AUDIT_MODE_VALUE_ENUM: Record<string, string> = {};
Object.values(WORKFLOW_AUDIT_MODE_OPTIONS).forEach((item: any) => {
  WORKFLOW_AUDIT_MODE_VALUE_ENUM[item?.value] = item?.label;
});

/** 密级 options */
export const SECURITY_LEVEL_OPTIONS: SelectProps['options'] = [
  { label: '公开', value: '公开' },
  { label: '内部', value: '内部' },
];

/** 密级 valueEnum */
export const SECURITY_LEVEL_VALUE_ENUM: Record<string, string> = {};
Object.values(SECURITY_LEVEL_OPTIONS).forEach((item: any) => {
  SECURITY_LEVEL_VALUE_ENUM[item?.value] = item?.label;
});

/** 型号类别 options */
export const MODEL_CATEGORY_OPTIONS: SelectProps['options'] = [
  { label: '运载火箭', value: '运载火箭' },
  { label: '战术', value: '战术' },
  { label: '战略', value: '战略' },
  { label: '卫星', value: '卫星' },
  { label: '飞船', value: '飞船' },
  { label: '航天器', value: '航天器' },
  { label: '无人机', value: '无人机' },
];

/** 型号类别 valueEnum */
export const MODEL_CATEGORY_VALUE_ENUM: Record<string, string> = {};
Object.values(MODEL_CATEGORY_OPTIONS).forEach((item: any) => {
  MODEL_CATEGORY_VALUE_ENUM[item?.value] = item?.label;
});

/** 项目来源 options */
export const PROJECT_SOURCE_OPTIONS: SelectProps['options'] = [
  { label: '装发', value: '装发' },
  { label: '科工局', value: '科工局' },
  { label: '陆军', value: '陆军' },
  { label: '海军', value: '海军' },
  { label: '空军', value: '空军' },
  { label: '火箭军', value: '火箭军' },
  { label: '预研', value: '预研' },
  { label: '综保', value: '综保' },
  { label: '横向', value: '横向' },
];

/** 项目来源 valueEnum */
export const PROJECT_SOURCE_VALUE_ENUM: Record<string, string> = {};
Object.values(PROJECT_SOURCE_OPTIONS).forEach((item: any) => {
  PROJECT_SOURCE_VALUE_ENUM[item?.value] = item?.label;
});

/** 隶属分类 options */
export const BELONGING_TYPE_OPTIONS: SelectProps['options'] = [
  { label: '弹', value: '弹' },
  { label: '箭', value: '箭' },
  { label: '星', value: '星' },
  { label: '船', value: '船' },
  { label: '器', value: '器' },
  { label: '地面', value: '地面' },
];

/** 隶属分类 valueEnum */
export const BELONGING_TYPE_VALUE_ENUM: Record<string, string> = {};
Object.values(BELONGING_TYPE_OPTIONS).forEach((item: any) => {
  BELONGING_TYPE_VALUE_ENUM[item?.value] = item?.label;
});

/** 所属体系 options */
export const BELONGING_SYSTEM_OPTIONS: SelectProps['options'] = [
  { label: '发射服务', value: '发射服务' },
  { label: '商业发射', value: '商业发射' },
  { label: '卫星组网', value: '卫星组网' },
  { label: '空间站', value: '空间站' },
  { label: '探月', value: '探月' },
  { label: '深空探测', value: '深空探测' },
  { label: '防御', value: '防御' },
];

/** 所属体系 valueEnum */
export const BELONGING_SYSTEM_VALUE_ENUM: Record<string, string> = {};
Object.values(BELONGING_SYSTEM_OPTIONS).forEach((item: any) => {
  BELONGING_SYSTEM_VALUE_ENUM[item?.value] = item?.label;
});

/** 研制阶段 options */
export const DEVELOPMENT_STAGE_OPTIONS: SelectProps['options'] = [
  { label: 'C', value: 'C' },
  { label: 'S', value: 'S' },
  { label: 'Z', value: 'Z' },
  { label: 'D', value: 'D' },
  { label: 'P', value: 'P' },
];

/** 研制阶段 valueEnum */
export const DEVELOPMENT_STAGE_VALUE_ENUM: Record<string, string> = {};
Object.values(DEVELOPMENT_STAGE_OPTIONS).forEach((item: any) => {
  DEVELOPMENT_STAGE_VALUE_ENUM[item?.value] = item?.label;
});

/** 工作阶段 options */
export const WORK_STAGE_OPTIONS: SelectProps['options'] = [
  { label: '立项论证', value: '立项论证' },
  { label: '方案设计', value: '方案设计' },
  { label: '单机研制', value: '单机研制' },
  { label: '发动机试车', value: '发动机试车' },
  { label: '总装测试', value: '总装测试' },
  { label: '整星测试', value: '整星测试' },
  { label: '发射场测试', value: '发射场测试' },
  { label: '飞行试验', value: '飞行试验' },
  { label: '对接试验', value: '对接试验' },
  { label: '发射场在轨测试', value: '发射场在轨测试' },
  { label: '在轨运行', value: '在轨运行' },
  { label: '使用保障', value: '使用保障' },
  { label: '其他', value: '其他' },
];

/** 工作阶段 valueEnum */
export const WORK_STAGE_VALUE_ENUM: Record<string, string> = {};
Object.values(WORK_STAGE_OPTIONS).forEach((item: any) => {
  WORK_STAGE_VALUE_ENUM[item?.value] = item?.label;
});

/** 项目分类 options */
export const PROJECT_TYPE_OPTIONS: SelectProps['options'] = [
  { label: '试制类', value: '试制类' },
  { label: '技术类', value: '技术类' },
  { label: '研究类', value: '研究类' },
  { label: '批产类', value: '批产类' },
];

/** 项目分类 valueEnum */
export const PROJECT_TYPE_VALUE_ENUM: Record<string, string> = {};
Object.values(PROJECT_TYPE_OPTIONS).forEach((item: any) => {
  PROJECT_TYPE_VALUE_ENUM[item?.value] = item?.label;
});
